import type { AnyGameModule } from '../../core/contracts'
import { getUserFirstName } from '../../platform/telegram'
import { useT } from '../../platform/i18n/useT'
import type { TKey } from '../../platform/i18n/i18nContext'
import { gameById } from '../../registry'
import { ScoreBlock } from './ScoreBlock'
import { disciplineName, gameTitle } from '../gameText'
import type { Progress } from '../useProgress'
import type { Workout } from '../useWorkout'
import './screens.css'

interface Props {
  progress: Progress
  workout: Workout
  onPlay: (game: AnyGameModule) => void
  onStartWorkout: () => void
}

/** Тренировка дня: программа, прогресс по ней, серия. */
/** Приветствие по времени суток. Шесть и восемнадцать - привычные границы. */
function greetingKey(hour: number): TKey {
  if (hour < 6 || hour >= 18) return 'home.evening'
  if (hour < 12) return 'home.morning'
  return 'home.day'
}

export function TodayScreen({ progress, workout, onPlay, onStartWorkout }: Props) {
  const { t } = useT()
  const name = getUserFirstName()
  const greeting = t(greetingKey(new Date().getHours()))
  const plan = workout.state?.plan.gameIds ?? []
  const completed = workout.state?.completed ?? []
  const left = plan.length - completed.length

  return (
    <>
      <header className="sc__head">
        <div className="sc__headline">
          <div className="sc__eyebrow">{t('home.workout')}</div>
          {workout.streak > 0 && (
            <span className="sc__streak">{t('common.streak', { n: workout.streak })}</span>
          )}
        </div>
        <h1 className="sc__h1">{name ? `${greeting},\n${name}` : greeting}</h1>
        <p className="sc__lead">
          {progress.weakest
            ? t('today.weak', { d: t(`discipline.${progress.weakest}`) })
            : t('today.sub')}
        </p>
      </header>

      <ScoreBlock progress={progress} />

      <div className="sc__label">
        {t('home.workout')} · {completed.length} / {plan.length}
      </div>

      {plan.map((id, i) => {
        const game = gameById(id)
        if (!game) return null
        const isDone = completed.includes(id)
        return (
          <button
            key={id}
            className={isDone ? 'sc__row sc__row--done' : 'sc__row'}
            onClick={() => onPlay(game)}
          >
            <span className="sc__num">{String(i + 1).padStart(2, '0')}</span>
            <span className="sc__meta">
              <b>{gameTitle(t, game)}</b>
              <small>{disciplineName(t, game)}</small>
            </span>
            <span className={isDone ? 'sc__arrow sc__arrow--done' : 'sc__arrow'}>
              {isDone ? '✓' : '→'}
            </span>
          </button>
        )
      })}

      {workout.done ? (
        <p className="sc__empty">{t('today.empty')}</p>
      ) : (
        <button className="sc__cta" onClick={onStartWorkout} disabled={workout.loading}>
          {completed.length === 0
            ? t('today.start')
            : t('home.continue') + ' · ' + t('home.left', { n: left })}
        </button>
      )}
    </>
  )
}
